import { DownOutlined, FilterOutlined } from "@ant-design/icons"
import { Button, Checkbox, Divider, Dropdown } from "antd"
import React, { useMemo, useState } from "react"

import { TextInput } from "../../../components/Input/TextInput.tsx"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector"
import {
  type CompanyPageKey,
  selectAccessibleBranches,
  selectIsCompanyMode,
  selectPageFilter,
  setPageFilter
} from "../redux/branches.slice.ts"

/**
 * One company page's own independent multi-select branch filter — never
 * the global selector, and never shared with any other page. Mandatory,
 * visible in the page's own main filter toolbar (not tucked into "More
 * filters") on every one of the five company-level pages while the global
 * selector is "All branches" — each page mounts its own instance of this
 * with its own `page` key, so Invoices' filter cannot affect Calendar's.
 *
 * Never accepts an empty selection: clearing every checkbox and pressing
 * Apply resolves to "All branches" rather than silently meaning nothing,
 * or worse, everything.
 */
export const PageBranchFilter = ({ page }: { page: CompanyPageKey }) => {
  const dispatch = useAppDispatch()
  const branches = useAppSelector(selectAccessibleBranches)
  const scope = useAppSelector(selectPageFilter(page))
  const isCompanyMode = useAppSelector(selectIsCompanyMode)
  const activeIds = branches.map((b) => b.id)

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  // Staged selection — only committed to redux on Apply, so Cancel (or
  // closing the dropdown without pressing Apply) discards it.
  const [staged, setStaged] = useState<string[]>(
    scope.type === "subset" ? scope.teamIds : activeIds
  )

  const openDropdown = (next: boolean) => {
    if (next) {
      setStaged(scope.type === "subset" ? scope.teamIds : activeIds)
      setSearch("")
    }
    setOpen(next)
  }

  // Prefixed "Branches:" so this never reads as the same control as the
  // header's own workspace selector (which shows "Company HQ" or a bare
  // branch name, with a building icon rather than this filter icon) — two
  // controls that could otherwise both show "All branches" side by side.
  const label = useMemo(() => {
    if (scope.type === "all") return "Branches: All branches"
    if (scope.teamIds.length === 1) {
      const name =
        branches.find((b) => b.id === scope.teamIds[0])?.name ??
        "1 branch selected"
      return `Branches: ${name}`
    }
    return `Branches: ${scope.teamIds.length} branches selected`
  }, [scope, branches])

  // Redundant while a specific branch is active in the global selector —
  // that page filter's state is untouched and simply restored on return
  // to "All branches", never cleared by hiding.
  if (!isCompanyMode) return null

  const filteredBranches = branches.filter((b) =>
    b.name.toLowerCase().includes(search.toLowerCase())
  )

  const toggle = (id: string) => {
    setStaged((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const allChecked = staged.length === activeIds.length
  const toggleAll = () => setStaged(allChecked ? [] : activeIds)

  const apply = () => {
    // An empty selection can never silently mean all branches — resolve it
    // to the explicit "all" scope instead.
    dispatch(
      setPageFilter({
        page,
        scope:
          staged.length === 0 || staged.length === activeIds.length
            ? { type: "all" }
            : { type: "subset", teamIds: staged }
      })
    )
    setOpen(false)
  }

  const dropdownContent = (
    <div
      className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 w-72"
      onClick={(e) => e.stopPropagation()}
    >
      <TextInput
        placeholder="Search branches"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-2"
        allowClear
        disableAutoCapitalize
      />
      <div className="flex items-center justify-between px-1 py-1">
        <Checkbox checked={allChecked} onChange={toggleAll}>
          <span className="font-medium">Select all</span>
        </Checkbox>
      </div>
      <Divider className="my-2" />
      <div className="max-h-64 overflow-y-auto flex flex-col gap-1">
        {filteredBranches.map((branch) => (
          <Checkbox
            key={branch.id}
            checked={staged.includes(branch.id)}
            onChange={() => toggle(branch.id)}
            className="px-1 py-1"
          >
            {branch.name}
          </Checkbox>
        ))}
        {filteredBranches.length === 0 && (
          <div className="text-gray-400 text-sm px-1 py-2">
            No branches match.
          </div>
        )}
      </div>
      <Divider className="my-2" />
      <div className="flex justify-end gap-2">
        <Button size="small" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button size="small" type="primary" onClick={apply}>
          Apply
        </Button>
      </div>
    </div>
  )

  return (
    <Dropdown
      open={open}
      onOpenChange={openDropdown}
      trigger={["click"]}
      dropdownRender={() => dropdownContent}
    >
      <Button
        icon={<FilterOutlined />}
        data-testid={`page-branch-filter-${page}`}
      >
        {label}
        <DownOutlined className="text-xs" />
      </Button>
    </Dropdown>
  )
}
